import type { Property } from 'csstype';

type Width = Property.Width;

export type Column = TextColumn | NumberColumn | DropdownColumn | DateColumn;

export type ColumnType = Column['type'];

interface BaseColumn {
  readonly id: string;
  readonly label: string;
  readonly width?: Width;
}

export interface TextColumn extends BaseColumn {
  readonly type: 'text';
  readonly editable: boolean;
}

export function isTextColumn(col: Column): col is TextColumn {
  return col.type === 'text';
}

export interface NumberColumn extends BaseColumn {
  readonly type: 'number';
  readonly editable: boolean;
  readonly min?: number;
  readonly max?: number;
  readonly step?: number;
}

export function isNumberColumn(col: Column): col is NumberColumn {
  return col.type === 'number';
}

export interface DropdownColumn extends BaseColumn {
  readonly type: 'dropdown';
  readonly editable: boolean;
  readonly options: readonly string[];
}

export function isDropdownColumn(col: Column): col is DropdownColumn {
  return col.type === 'dropdown';
}

export function getDropdownOptions(col: Column): readonly string[] | null {
  return isDropdownColumn(col) ? col.options : null;
}

export interface DateColumn extends BaseColumn {
  readonly type: 'date';
  readonly editable: boolean;
  readonly min?: string;
  readonly max?: string;
  readonly format?: 'date' | 'datetime' | 'time';
}

export function isDateColumn(col: Column): col is DateColumn {
  return col.type === 'date';
}

export function validateCellValue(value: any, column: Column): any {
  if (value === null || value === undefined || value === '') return undefined;

  switch (column.type) {
    case 'number': {
      const num = Number(value);
      if (isNaN(num)) return undefined;
      if (column.min !== undefined && num < column.min) return undefined;
      if (column.max !== undefined && num > column.max) return undefined;
      return num;
    }
    case 'dropdown': {
      const strValue = String(value);
      return column.options.includes(strValue) ? strValue : undefined;
    }
    case 'date': {
      const strValue = String(value);
      const date = new Date(strValue);
      if (isNaN(date.getTime())) return undefined;
      if (column.min && strValue < column.min) return undefined;
      if (column.max && strValue > column.max) return undefined;
      return strValue;
    }
    case 'text':
    default:
      return String(value);
  }
}
